import { useContext, useMemo } from 'react';

import FilterPlatesContext from 'contexts/FilterPlatesContext';
import ReportContext from 'contexts/ReportContext';
import filterPlateTypeCheckers from 'types/filterPlateTypeCheckers';

export interface PinnedFilterSlot {
  pin: ReportPinnedFilters.Field;
  plate?: FilterPlateType;
}

const isPlateForPin = ({
  plate,
  pin,
}: {
  plate: FilterPlateType;
  pin: ReportPinnedFilters.Field;
}) => {
  if (!filterPlateTypeCheckers.isFixed(plate)) {
    return false;
  }

  const { field, dataset } = plate.fixedValue;
  if (field !== pin.field) {
    return false;
  }

  return !pin.dataset || !dataset || dataset === pin.dataset;
};

/**
 * Pairs each of the report's pinned filter fields with the drill-down
 * filtering on that field (if any). Values for pinned fields are ordinary
 * drill-downs, so reset, export and popups carry them as usual.
 */
const usePinnedFilterSlots = () => {
  const { report } = useContext(ReportContext);
  const { drillDowns } = useContext(FilterPlatesContext);
  const pins = report?.pinnedFilterFields;

  return useMemo(() => {
    const slots: PinnedFilterSlot[] = [];
    const hiddenDrillDownIds: string[] = [];

    (pins || []).forEach((pin) => {
      const plate = drillDowns.find(
        (d) =>
          !hiddenDrillDownIds.includes(d.id) &&
          isPlateForPin({ plate: d, pin }),
      );
      if (plate) {
        hiddenDrillDownIds.push(plate.id);
      }
      slots.push({ pin, plate });
    });

    return { slots, hiddenDrillDownIds };
  }, [drillDowns, pins]);
};

export default usePinnedFilterSlots;
